import React, { createContext, useContext, useMemo, useCallback, ReactNode } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme, useColorScheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import type {} from '@mui/x-data-grid/themeAugmentation';
import type {} from '@mui/x-date-pickers/themeAugmentation';

type ThemeMode = 'light' | 'dark';

interface ThemeContextType {
  mode: ThemeMode;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// GWD Design Tokens based on the GWD webcomponents library
const gwdTokens = {
  light: {
    brandMain: '#074842',
    brandDark: '#1F988E',
    brandAccent: 'rgb(235, 105, 45)',
    brandSoft: 'rgb(233, 229, 226)',
    brandLite: 'rgb(255, 255, 255)',
    baseBgPrimary: 'rgb(255, 255, 255)',
    baseBgSecondary: 'rgb(237, 234, 232)',
    baseTxtPrimary: 'rgb(0, 0, 0)',
    baseTxtSecondary: 'rgb(89, 89, 89)',
    emphasisTxt: 'rgb(0, 72, 195)',
    errorFill: 'rgb(223, 42, 42)',
    errorOnFill: 'rgb(255, 255, 255)',
    errorTxt: 'rgb(173, 26, 26)',
    infoFill: 'rgb(123, 87, 255)',
    infoOnFill: 'rgb(255, 255, 255)',
    infoTxt: 'rgb(98, 63, 222)',
    successFill: 'rgb(90, 191, 189)',
    successOnFill: 'rgb(0, 0, 0)',
    successTxt: 'rgb(17, 105, 102)',
    warningFill: 'rgb(243, 165, 129)',
    warningOnFill: 'rgb(0, 0, 0)',
    warningTxt: 'rgb(166, 63, 28)',
    surfaceBase: 'rgb(245, 245, 245)',
    surfaceOverlay: 'rgb(255, 255, 255)',
    surfaceBrand: 'rgb(205, 219, 254)',
    neutral: {
      0: 'rgb(255, 255, 255)',
      100: 'rgb(241, 241, 241)',
      200: 'rgb(212, 212, 212)',
      300: 'rgb(171, 171, 171)',
      400: 'rgb(145, 145, 145)',
      500: 'rgb(124, 124, 124)',
      600: 'rgb(107, 107, 107)',
      700: 'rgb(83, 83, 83)',
      800: 'rgb(60, 60, 60)',
      900: 'rgb(38, 38, 38)',
      1000: 'rgb(0, 0, 0)',
    }
  },
  dark: {
    brandMain: '#28BAAD',
    brandDark: '#1F988E',
    brandAccent: 'rgb(235, 105, 45)',
    brandSoft: 'rgb(233, 229, 226)',
    brandLite: 'rgb(255, 255, 255)',
    baseBgPrimary: 'rgb(1, 1, 1)',
    baseBgSecondary: 'rgb(43, 43, 43)',
    baseTxtPrimary: 'rgb(240, 240, 240)',
    baseTxtSecondary: 'rgb(156, 156, 156)',
    brandTxt: 'rgb(221, 221, 234)',
    emphasisTxt: 'rgb(82, 145, 255)',
    errorFill: 'rgb(223, 42, 42)',
    errorOnFill: 'rgb(255, 255, 255)',
    errorTxt: 'rgb(236, 126, 126)',
    infoFill: 'rgb(141, 133, 255)',
    infoOnFill: 'rgb(0, 0, 0)',
    infoTxt: 'rgb(141, 133, 255)',
    successFill: 'rgb(90, 191, 189)',
    successOnFill: 'rgb(0, 0, 0)',
    successTxt: 'rgb(90, 191, 189)',
    warningFill: 'rgb(243, 165, 129)',
    warningOnFill: 'rgb(37, 28, 1)',
    warningTxt: 'rgb(243, 165, 129)',
    surfaceBase: 'rgb(0, 0, 0)',
    surfaceOverlay: 'rgb(26, 26, 26)',
    surfaceBrand: 'rgb(27, 42, 80)',
    neutral: {
      0: 'rgb(0, 0, 0)',
      100: 'rgb(38, 38, 38)',
      200: 'rgb(60, 60, 60)',
      300: 'rgb(83, 83, 83)',
      400: 'rgb(107, 107, 107)',
      500: 'rgb(124, 124, 124)',
      600: 'rgb(145, 145, 145)',
      700: 'rgb(171, 171, 171)',
      800: 'rgb(212, 212, 212)',
      900: 'rgb(241, 241, 241)',
      1000: 'rgb(255, 255, 255)',
    }
  }
};

interface ThemeProviderProps {
  children: ReactNode;
}

// Create theme with CSS variables - created once and doesn't change when mode switches
// This is the MUI v7 recommended approach for better performance
const theme = createTheme({
  cssVariables: {
    colorSchemeSelector: 'class',
  },
  defaultColorScheme: 'light',
  colorSchemes: {
    light: {
      palette: {
        primary: {
          main: gwdTokens.light.brandMain,
          dark: gwdTokens.light.brandDark,
          contrastText: gwdTokens.light.brandLite,
        },
        secondary: {
          main: gwdTokens.light.brandAccent,
          contrastText: gwdTokens.light.brandLite,
        },
        error: {
          main: gwdTokens.light.errorFill,
          contrastText: gwdTokens.light.brandLite,
        },
        warning: {
          main: gwdTokens.light.warningFill,
          contrastText: gwdTokens.light.baseTxtPrimary,
        },
        info: {
          main: gwdTokens.light.infoFill,
          contrastText: gwdTokens.light.brandLite,
        },
        success: {
          main: gwdTokens.light.successFill,
          contrastText: gwdTokens.light.baseTxtPrimary,
        },
        background: {
          default: gwdTokens.light.baseBgPrimary,
          paper: gwdTokens.light.surfaceOverlay,
        },
        text: {
          primary: gwdTokens.light.baseTxtPrimary,
          secondary: gwdTokens.light.baseTxtSecondary,
        },
        divider: gwdTokens.light.neutral[200],
        grey: gwdTokens.light.neutral,
      },
    },
    dark: {
      palette: {
        primary: {
          main: gwdTokens.dark.brandMain,
          dark: gwdTokens.dark.brandDark,
          contrastText: gwdTokens.dark.brandLite,
        },
        secondary: {
          main: gwdTokens.dark.brandAccent,
          contrastText: gwdTokens.dark.brandLite,
        },
        error: {
          main: gwdTokens.dark.errorFill,
          contrastText: gwdTokens.dark.brandLite,
        },
        warning: {
          main: gwdTokens.dark.warningFill,
          contrastText: gwdTokens.dark.baseTxtPrimary,
        },
        info: {
          main: gwdTokens.dark.infoFill,
          contrastText: gwdTokens.dark.brandLite,
        },
        success: {
          main: gwdTokens.dark.successFill,
          contrastText: gwdTokens.dark.baseTxtPrimary,
        },
        background: {
          default: gwdTokens.dark.baseBgPrimary,
          paper: gwdTokens.dark.surfaceOverlay,
        },
        text: {
          primary: gwdTokens.dark.baseTxtPrimary,
          secondary: gwdTokens.dark.baseTxtSecondary,
        },
        divider: gwdTokens.dark.neutral[200],
        grey: gwdTokens.dark.neutral,
      },
    },
  },
  typography: {
    fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: { fontFamily: '"ES Klarheit Kurrent", "Poppins", sans-serif' },
    h2: { fontFamily: '"ES Klarheit Kurrent", "Poppins", sans-serif' },
    h3: { fontFamily: '"ES Klarheit Kurrent", "Poppins", sans-serif' },
    h4: { fontFamily: '"ES Klarheit Kurrent", "Poppins", sans-serif' },
    h5: { fontFamily: '"ES Klarheit Kurrent", "Poppins", sans-serif' },
    h6: { fontFamily: '"ES Klarheit Kurrent", "Poppins", sans-serif' },
  },
  shape: {
    borderRadius: 4,
  },
  spacing: 8,
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.vars.palette.primary.main,
          color: theme.vars.palette.primary.contrastText,
          boxShadow: theme.shadows[2],
          ...theme.applyStyles('dark', {
            backgroundColor: gwdTokens.dark.brandMain,
          }),
        }),
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 400,
          borderRadius: '4px',
        },
        containedPrimary: ({ theme }) => ({
          backgroundColor: theme.vars.palette.primary.main,
          color: theme.vars.palette.primary.contrastText,
          '&:hover': {
            backgroundColor: theme.vars.palette.primary.dark,
          },
        }),
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: ({ theme }) => ({
          backgroundColor: theme.vars.palette.background.paper,
          border: `1px solid ${theme.vars.palette.divider}`,
          borderRadius: '4px',
          boxShadow: theme.shadows[4],
          marginTop: '8px',
        }),
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: '12px 16px',
          fontSize: '14px',
          fontWeight: 400,
          color: theme.vars.palette.text.primary,
          minHeight: 'auto',
          '&:hover': {
            backgroundColor: `color-mix(in srgb, ${theme.vars.palette.secondary.main} 12%, transparent)`,
            boxShadow: `inset 3px 0 0 ${theme.vars.palette.secondary.main}`,
            ...theme.applyStyles('dark', {
              backgroundColor: `color-mix(in srgb, ${theme.vars.palette.secondary.main} 16%, transparent)`,
            }),
          },
          '&:focus': {
            backgroundColor: `color-mix(in srgb, ${theme.vars.palette.secondary.main} 12%, transparent)`,
            boxShadow: `inset 3px 0 0 ${theme.vars.palette.secondary.main}`,
            ...theme.applyStyles('dark', {
              backgroundColor: `color-mix(in srgb, ${theme.vars.palette.secondary.main} 16%, transparent)`,
            }),
          },
          '&.Mui-selected': {
            backgroundColor: `color-mix(in srgb, ${theme.vars.palette.secondary.main} 12%, transparent)`,
            boxShadow: `inset 3px 0 0 ${theme.vars.palette.secondary.main}`,
            color: theme.vars.palette.text.primary,
            fontWeight: 500,
            '&:hover': {
              backgroundColor: `color-mix(in srgb, ${theme.vars.palette.secondary.main} 18%, transparent)`,
              boxShadow: `inset 3px 0 0 ${theme.vars.palette.secondary.main}`,
              ...theme.applyStyles('dark', {
                backgroundColor: `color-mix(in srgb, ${theme.vars.palette.secondary.main} 22%, transparent)`,
              }),
            },
            ...theme.applyStyles('dark', {
              backgroundColor: `color-mix(in srgb, ${theme.vars.palette.secondary.main} 16%, transparent)`,
              color: theme.vars.palette.text.primary,
              '&:hover': {
                backgroundColor: `color-mix(in srgb, ${theme.vars.palette.secondary.main} 22%, transparent)`,
              },
            }),
          },
        }),
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.vars.palette.primary.dark,
          color: theme.vars.palette.primary.contrastText,
          border: `2px solid ${theme.vars.palette.divider}`,
          fontWeight: 500,
        }),
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.vars.palette.background.paper,
          color: theme.vars.palette.text.primary,
        }),
      },
    },
    MuiAlert: {
      styleOverrides: {
        standardError: ({ theme }) => ({
          backgroundColor: theme.vars.palette.error.main,
          color: theme.vars.palette.error.contrastText,
          '& .MuiAlert-icon': {
            color: theme.vars.palette.error.contrastText,
          },
        }),
        standardWarning: ({ theme }) => ({
          backgroundColor: theme.vars.palette.warning.main,
          color: theme.vars.palette.warning.contrastText,
          '& .MuiAlert-icon': {
            color: theme.vars.palette.warning.contrastText,
          },
        }),
        standardInfo: ({ theme }) => ({
          backgroundColor: theme.vars.palette.info.main,
          color: theme.vars.palette.info.contrastText,
          '& .MuiAlert-icon': {
            color: theme.vars.palette.info.contrastText,
          },
        }),
        standardSuccess: ({ theme }) => ({
          backgroundColor: theme.vars.palette.success.main,
          color: theme.vars.palette.success.contrastText,
          '& .MuiAlert-icon': {
            color: theme.vars.palette.success.contrastText,
          },
        }),
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        option: ({ theme }) => ({
          '&:hover': {
            backgroundColor: `color-mix(in srgb, ${theme.vars.palette.secondary.main} 12%, transparent)`,
            ...theme.applyStyles('dark', {
              backgroundColor: `color-mix(in srgb, ${theme.vars.palette.secondary.main} 16%, transparent)`,
            }),
          },
          '&.Mui-focused': {
            backgroundColor: `color-mix(in srgb, ${theme.vars.palette.secondary.main} 12%, transparent) !important`,
            boxShadow: `inset 3px 0 0 ${theme.vars.palette.secondary.main}`,
            ...theme.applyStyles('dark', {
              backgroundColor: `color-mix(in srgb, ${theme.vars.palette.secondary.main} 16%, transparent) !important`,
            }),
          },
          '&[aria-selected="true"]': {
            backgroundColor: `color-mix(in srgb, ${theme.vars.palette.secondary.main} 12%, transparent) !important`,
            boxShadow: `inset 3px 0 0 ${theme.vars.palette.secondary.main}`,
            ...theme.applyStyles('dark', {
              backgroundColor: `color-mix(in srgb, ${theme.vars.palette.secondary.main} 16%, transparent) !important`,
            }),
            '&:hover': {
              backgroundColor: `color-mix(in srgb, ${theme.vars.palette.secondary.main} 18%, transparent) !important`,
              boxShadow: `inset 3px 0 0 ${theme.vars.palette.secondary.main}`,
              ...theme.applyStyles('dark', {
                backgroundColor: `color-mix(in srgb, ${theme.vars.palette.secondary.main} 22%, transparent) !important`,
              }),
            },
          },
          '&.Mui-focused[aria-selected="true"]': {
            backgroundColor: `color-mix(in srgb, ${theme.vars.palette.secondary.main} 18%, transparent) !important`,
            boxShadow: `inset 3px 0 0 ${theme.vars.palette.secondary.main}`,
            ...theme.applyStyles('dark', {
              backgroundColor: `color-mix(in srgb, ${theme.vars.palette.secondary.main} 22%, transparent) !important`,
            }),
          },
        }),
      },
    },
    MuiPickersDay: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&.Mui-selected': {
            backgroundColor: `${theme.vars.palette.secondary.main} !important`,
            color: `${theme.vars.palette.secondary.contrastText} !important`,
            fontWeight: 600,
            '&:hover': {
              backgroundColor: `${theme.vars.palette.secondary.dark} !important`,
            },
            '&:focus': {
              backgroundColor: `${theme.vars.palette.secondary.main} !important`,
            },
          },
          '&:focus.Mui-selected': {
            backgroundColor: `${theme.vars.palette.secondary.main} !important`,
          },
        }),
      },
    },
    MuiMultiSectionDigitalClockSection: {
      styleOverrides: {
        root: ({ theme }) => ({
          '& .MuiButtonBase-root': {
            '&.Mui-selected': {
              backgroundColor: `color-mix(in srgb, ${theme.vars.palette.secondary.main} 12%, transparent) !important`,
              
              '&:hover': {
                backgroundColor: `${theme.vars.palette.secondary.dark} !important`,
              },
              '&:focus': {
                backgroundColor: theme.vars.palette.secondary.main,
              },
            },
          },
        }),
        item: ({ theme }) => ({
          '&.Mui-selected': {
            backgroundColor: `color-mix(in srgb, ${theme.vars.palette.secondary.main} 12%, transparent) !important`,
            color: `${theme.vars.palette.text.primary} !important`,
            '&:hover': {
              backgroundColor: theme.vars.palette.secondary.dark,
            },
            '&:focus': {
              backgroundColor: theme.vars.palette.secondary.main,
            },
          },
        }),
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: ({ theme }) => ({
          zIndex: 0,
          minHeight: '50vh',
          borderColor: theme.vars.palette.divider,
          border: `1px solid ${theme.vars.palette.divider}`,
          borderRadius: '4px',
          fontFamily: '"Poppins", "Questrial", sans-serif',
          '--DataGrid-rowBorderColor': theme.vars.palette.divider,
          '.MuiDataGrid-row': {
            color: theme.vars.palette.text.primary,
            backgroundColor: theme.vars.palette.background.paper,
          },
          '.MuiDataGrid-row:hover': {
            backgroundColor: `color-mix(in srgb, ${theme.vars.palette.secondary.main} 4%, transparent)`,
            ...theme.applyStyles('dark', {
              backgroundColor: `color-mix(in srgb, ${theme.vars.palette.secondary.main} 6%, transparent)`,
            }),
          },
          '.MuiDataGrid-cell': {
          },
          '.MuiDataGrid-columnHeaderTitle': {
            fontSize: '0.800rem',
            fontWeight: '700',
          },
          '& .MuiDataGrid-columnHeaders': {
            position: 'sticky',
          },
          '.MuiDataGrid-selectedRowCount': {
            color: theme.vars.palette.text.primary,
          },
          '.MuiCheckbox-root': {
            color: theme.vars.palette.text.primary,
          },
          '.MuiDataGrid-filler': {
            backgroundColor: theme.vars.palette.background.paper,
          },
          '.MuiDataGrid-scrollbarFiller': {
            backgroundColor: theme.vars.palette.background.paper,
          },
          '.MuiDataGrid-row.Mui-selected': {
            backgroundColor: `color-mix(in srgb, ${theme.vars.palette.secondary.main} 12%, transparent)`,
            boxShadow: `inset 3px 0 0 ${theme.vars.palette.secondary.main}`,
            ...theme.applyStyles('dark', {
              backgroundColor: `color-mix(in srgb, ${theme.vars.palette.secondary.main} 16%, transparent)`,
            }),
          },
          '.MuiDataGrid-row .MuiCheckbox-root .MuiSvgIcon-root': {
            fill: theme.vars.palette.text.secondary,
          },
          '.MuiDataGrid-row.Mui-selected .MuiCheckbox-root .MuiSvgIcon-root': {
            fill: theme.vars.palette.secondary.main,
          },
          '.MuiDataGrid-row.Mui-selected:hover': {
            backgroundColor: `color-mix(in srgb, ${theme.vars.palette.secondary.main} 18%, transparent)`,
            boxShadow: `inset 3px 0 0 ${theme.vars.palette.secondary.main}`,
            ...theme.applyStyles('dark', {
              backgroundColor: `color-mix(in srgb, ${theme.vars.palette.secondary.main} 22%, transparent)`,
            }),
          },
          '.MuiDataGrid-footerContainer': {
            borderColor: 'transparent',
          },
          '.MuiDataGrid-overlay': {
            backgroundColor: theme.vars.palette.background.paper,
            color: theme.vars.palette.text.primary,
          },
        }),
        columnHeader: ({ theme }) => ({
          backgroundColor: gwdTokens.light.surfaceBase,
          ...theme.applyStyles('dark', {
            backgroundColor: gwdTokens.dark.surfaceBase,
          }),
          '.MuiSvgIcon-root': {
            color: theme.vars.palette.text.primary,
          },
        }),
        columnHeaderTitle: ({ theme }) => ({
          color: theme.vars.palette.text.primary,
        }),
        footerContainer: ({ theme }) => ({
          backgroundColor: gwdTokens.light.surfaceBase,
          ...theme.applyStyles('dark', {
            backgroundColor: gwdTokens.dark.surfaceBase,
          }),
        }),
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: ({ theme }) => ({
          '.MuiSvgIcon-root': {
            color: theme.vars.palette.text.primary,
          },
        }),
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        toolbar: ({ theme }) => ({
          color: theme.vars.palette.text.primary,
          '.MuiButtonBase-root': {
            color: theme.vars.palette.text.primary,
          },
        }),
        selectIcon: ({ theme }) => ({
          color: theme.vars.palette.text.primary,
        }),
        root: ({ theme }) => ({
          '.MuiSvgIcon-colorPrimary': {
            color: theme.vars.palette.text.primary,
          },
        }),
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          '.MuiIconButton-colorPrimary': {
            color: theme.vars.palette.primary.main,
          },
          '.MuiIconButton-colorError': {
            color: gwdTokens.light.errorTxt,
            ...theme.applyStyles('dark', {
              color: gwdTokens.dark.errorTxt,
            }),
          },
          '.MuiIconButton-colorInfo': {
            color: gwdTokens.light.infoTxt,
            ...theme.applyStyles('dark', {
              color: gwdTokens.dark.infoTxt,
            }),
          },
          '.MuiIconButton-colorSuccess': {
            color: gwdTokens.light.successTxt,
            ...theme.applyStyles('dark', {
              color: gwdTokens.dark.successTxt,
            }),
          },
        }),
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: ({ theme }) => ({
          color: theme.vars.palette.text.primary,
          backgroundColor: theme.vars.palette.background.paper,
        }),
        icon: ({ theme }) => ({
          color: theme.vars.palette.text.primary,
        }),
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.vars.palette.text.primary,
          '& .MuiInputBase-input': {
            color: theme.vars.palette.text.primary,
          },
        }),
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.vars.palette.background.paper,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.vars.palette.divider,
            ...theme.applyStyles('dark', {
              borderColor: gwdTokens.dark.neutral[500], // Increased from 400 for better visibility
            }),
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.vars.palette.text.secondary,
            ...theme.applyStyles('dark', {
              borderColor: gwdTokens.dark.neutral[600],
            }),
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.vars.palette.primary.main,
          },
          '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
            ...theme.applyStyles('dark', {
              borderColor: gwdTokens.dark.neutral[300],
            }),
          },
        }),
      },
    },
  },
});

// eslint-disable-next-line react-refresh/only-export-components
export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

// Internal component that uses useColorScheme hook
// This must be inside the MUI ThemeProvider
function ThemeContextProvider({ children }: { children: ReactNode }) {
  const { mode: muiMode, setMode } = useColorScheme();
  
  const mode = (muiMode || 'light') as ThemeMode;

  const toggleTheme = useCallback(() => {
    setMode(mode === 'light' ? 'dark' : 'light');
  }, [mode, setMode]);

  const contextValue = useMemo<ThemeContextType>(() => ({
    mode,
    toggleTheme,
  }), [mode, toggleTheme]);

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  return (
    <MuiThemeProvider theme={theme} defaultMode="light">
      <CssBaseline enableColorScheme />
      <ThemeContextProvider>
        {children}
      </ThemeContextProvider>
    </MuiThemeProvider>
  );
};